import { Injectable } from '@angular/core';
import Parse from 'parse';
import { environment } from 'environments/environment';
import { ParseUserService } from '../user/parse-user.service';

// Define interfaces for Parse internal types that aren't properly exposed in the TypeScript definitions
interface ParseRESTController {
  ajax: (...args: any[]) => Promise<any>;
  _sessionExpiryHooked?: boolean;
}

interface ParseCoreManager {
  getRESTController: () => ParseRESTController;
}

/**
 * Sends every Parse SDK call that fails with an invalid session token to the
 * session-expired flow (sign-out and redirect to sign-in). Runs at app start
 * (app.config.ts), so pages that never inject ParseService are covered too.
 * Safe to call more than once.
 */
export function installSessionExpiryHook(parseUserService: ParseUserService): void {
  try {
    // Use type assertion to access the CoreManager
    const coreManager = Parse.CoreManager as unknown as ParseCoreManager;
    const restController = coreManager.getRESTController();
    if (!restController?.ajax || restController._sessionExpiryHooked) return;

    const originalAjax = restController.ajax;
    // Pass every argument through: Parse 8 sends request options as the fifth.
    restController.ajax = function(...args: any[]) {
      return originalAjax.apply(this, args)
        .catch(error => {
          // Check multiple error formats — at the AJAX layer the error shape
          // may differ from a Parse.Error (e.g. raw response JSON, wrapped response, etc.)
          const errorCode = error?.code ?? error?.response?.code ?? error?.responseJSON?.code;
          // The sign-out call itself fails the same way; it must not restart the flow.
          const isLogout = String(args[1] ?? '').endsWith('/logout');
          if (errorCode === Parse.Error.INVALID_SESSION_TOKEN && !isLogout) {
            console.warn('Invalid session token detected in Parse AJAX call, triggering session expiration');
            parseUserService.onUserSessionExpired();
          }
          throw error;
        });
    };
    restController._sessionExpiryHooked = true;
  } catch (e) {
    console.error('Error setting up Parse error handler:', e);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ParseService {

  constructor(private _parseUserService: ParseUserService) {
    this.initParse();
  }

  initParse(): void {
    // We're disabling LiveQuery completely due to WebSocket connection issues
    // Instead, we're using a polling approach for real-time updates
    
    // Now initialize Parse without LiveQuery
    (Parse as any).initialize(environment.appId);
    Parse.serverURL = environment.serverUrl;
    
    // Explicitly disable LiveQuery by setting to an empty string
    Parse.liveQueryServerURL = '';
    
    // Add a global error handler for Parse errors
    installSessionExpiryHook(this._parseUserService);
  }

  getCollectionsCount(): Promise<{ [key: string]: number }> {
    return Parse.Cloud.run('getCollectionsCount');
  }

  getCollectionsByRegion(region: string): Promise<any> {
    return Parse.Cloud.run('getCollectionsByRegion', { region });
  }

  /**
   * Upload a file to Parse server
   * @param data File data or base64 string
   * @param isBase64 Whether the data is a base64 string
   * @returns Promise with the uploaded Parse.File
   */
  upload(data: any, isBase64: boolean = true): Promise<Parse.File> {
    const file = isBase64 ? { base64: data } : data;
    const fileName = data.name || data.type?.replace('/', '.') || 'image.jpg';
    return new Parse.File(fileName, file).save();
  }
}
